import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { ClockWidget } from '@/pages/dashboard/widgets/ClockWidget.tsx'
import { formatDate, formatDuration } from '@/lib/utils.ts'
import { Pause, Play, Users, Clock, BarChart3 } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type ColumnDef,
} from '@/components/ui'

interface TeamMember {
  id: string
  name: string
  status: 'working' | 'paused' | 'offline'
  since?: string
}

interface OvertimeData {
  totalOvertimeMinutes: number
  weeklyHours: number
  targetWeeklyHours: number
  monthBalance: number
}

interface TimeEntry {
  id: string
  startTime: string
  endTime?: string
  durationMinutes?: number
  description?: string
}

function formatMinutesToHours(mins: number) {
  const h = Math.floor(Math.abs(mins) / 60)
  const m = Math.abs(mins) % 60
  const sign = mins < 0 ? '-' : '+'
  return `${sign}${h}h ${String(m).padStart(2, '0')}min`
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

export function ZeiterfassungPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'entries' | 'team'>('entries')

  const { data: entries, isLoading } = useQuery<TimeEntry[]>({
    queryKey: ['time', 'week'],
    queryFn: () => apiClient.get('/time/week').then((r) => r.data),
  })

  const { data: overtime } = useQuery<OvertimeData>({
    queryKey: ['time', 'overtime'],
    queryFn: () => apiClient.get('/time/overtime').then((r) => r.data),
    retry: false,
  })

  const { data: teamStatus = [] } = useQuery<TeamMember[]>({
    queryKey: ['time', 'team-status'],
    queryFn: () => apiClient.get('/time/team-status').then((r) => r.data),
    enabled: activeTab === 'team',
    refetchInterval: 30000,
    retry: false,
  })

  const pauseStart = useMutation({
    mutationFn: () => apiClient.post('/time/pause', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time'] })
    },
  })

  const pauseEnd = useMutation({
    mutationFn: () => apiClient.post('/time/pause/end', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time'] })
    },
  })

  const isPaused = teamStatus.some((m) => m.status === 'paused')

  const entryRows = useMemo(() => (entries ?? []).slice(0, 20), [entries])

  const entryColumns = useMemo<ColumnDef<TimeEntry, unknown>[]>(
    () => [
      {
        accessorKey: 'startTime',
        header: 'Datum',
        size: 110,
        cell: ({ getValue }) => (
          <span className="text-gray-500">{formatDate(getValue() as string)}</span>
        ),
      },
      {
        id: 'from',
        header: 'Von',
        size: 80,
        cell: ({ row }) => formatTime(row.original.startTime),
      },
      {
        id: 'to',
        header: 'Bis',
        size: 80,
        cell: ({ row }) => (row.original.endTime ? formatTime(row.original.endTime) : '–'),
      },
      {
        accessorKey: 'durationMinutes',
        header: 'Dauer',
        size: 90,
        cell: ({ getValue }) => {
          const v = getValue() as number | undefined
          return <span className="font-mono text-caption">{v ? formatDuration(v) : '–'}</span>
        },
      },
      {
        accessorKey: 'description',
        header: 'Beschreibung',
        cell: ({ getValue }) => (
          <span className="text-gray-600 truncate block max-w-md">
            {(getValue() as string | undefined) ?? '–'}
          </span>
        ),
      },
    ],
    [],
  )

  const teamColumns = useMemo<ColumnDef<TeamMember, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Mitarbeiter',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'w-2 h-2 rounded-full flex-shrink-0',
                row.original.status === 'working'
                  ? 'bg-green-500'
                  : row.original.status === 'paused'
                    ? 'bg-amber-500'
                    : 'bg-gray-300',
              )}
              aria-hidden
            />
            <span className="font-medium text-gray-800">{row.original.name}</span>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        cell: ({ getValue }) => {
          const s = getValue() as TeamMember['status']
          return <StatusBadge status={s} variant={s === 'offline' ? 'neutral' : undefined} />
        },
      },
      {
        accessorKey: 'since',
        header: 'Seit',
        size: 120,
        cell: ({ getValue }) => {
          const v = getValue() as string | undefined
          return v ? <span className="text-gray-500">{formatTime(v)}</span> : '–'
        },
      },
    ],
    [],
  )

  const progress = overtime
    ? Math.min(100, (overtime.weeklyHours / overtime.targetWeeklyHours) * 100)
    : 0
  const overBudget = overtime ? overtime.weeklyHours / overtime.targetWeeklyHours > 1 : false

  return (
    <div className="space-y-6">
      <PageHeader title="Zeiterfassung" description="Zeiteinträge, Pausen, Team-Status und Überstunden." />

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-4 space-y-4">
          <ClockWidget />

          <Card>
            <Card.Body className="space-y-3">
              <h3 className="text-small font-semibold text-gray-700">Pause</h3>
              {isPaused ? (
                <Button
                  variant="primary"
                  fullWidth
                  leadingIcon={<Play className="w-4 h-4" />}
                  loading={pauseEnd.isPending}
                  onClick={() => pauseEnd.mutate()}
                >
                  {pauseEnd.isPending ? 'Beende Pause …' : 'Pause beenden'}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  fullWidth
                  leadingIcon={<Pause className="w-4 h-4" />}
                  loading={pauseStart.isPending}
                  onClick={() => pauseStart.mutate()}
                >
                  {pauseStart.isPending ? 'Starte Pause …' : 'Pause starten'}
                </Button>
              )}
            </Card.Body>
          </Card>

          {overtime ? (
            <Card>
              <Card.Body className="space-y-3">
                <h3 className="text-small font-semibold text-gray-700 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-brand-500" />
                  Überstunden
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-caption text-gray-500">Überstunden gesamt</span>
                    <span
                      className={cn(
                        'text-small font-bold',
                        overtime.totalOvertimeMinutes >= 0 ? 'text-green-600' : 'text-red-600',
                      )}
                    >
                      {formatMinutesToHours(overtime.totalOvertimeMinutes)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-caption text-gray-500">Diese Woche</span>
                    <span className="text-small font-medium text-gray-700">
                      {(overtime.weeklyHours ?? 0).toFixed(1)}h / {overtime.targetWeeklyHours ?? 40}h
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className={cn(
                        'h-2 rounded-full transition-all',
                        overBudget ? 'bg-red-500' : 'bg-brand-500',
                      )}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-caption text-gray-500">Monatssaldo</span>
                    <span
                      className={cn(
                        'text-small font-medium',
                        overtime.monthBalance >= 0 ? 'text-green-600' : 'text-red-600',
                      )}
                    >
                      {formatMinutesToHours(overtime.monthBalance)}
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ) : null}
        </div>

        <div className="col-span-12 md:col-span-8">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'entries' | 'team')}>
            <TabsList>
              <TabsTrigger value="entries">
                <Clock className="w-3.5 h-3.5" />
                Zeiteinträge
              </TabsTrigger>
              <TabsTrigger value="team">
                <Users className="w-3.5 h-3.5" />
                Team-Status
              </TabsTrigger>
            </TabsList>

            <TabsContent value="entries">
              <DataTable
                data={entryRows}
                columns={entryColumns}
                getRowId={(row) => row.id}
                loading={isLoading}
                emptyState={
                  <EmptyState
                    title="Keine Zeiteinträge"
                    description="Es wurden noch keine Zeiteinträge für diese Woche erfasst."
                    icon={<Clock />}
                  />
                }
              />
            </TabsContent>

            <TabsContent value="team">
              <DataTable
                data={teamStatus}
                columns={teamColumns}
                getRowId={(row) => row.id}
                emptyState={
                  <EmptyState
                    title="Keine Team-Daten verfügbar"
                    description="Es konnten keine Team-Status-Informationen geladen werden."
                    icon={<Users />}
                  />
                }
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
